import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  userAPI,
  driverAPI,
  translateBackendMessage,
} from "../../api/apiSystem";
import "./NotificationBell.css";

const POLL_INTERVAL = 60_000; // refresh every 60 seconds

function getNotifAPI() {
  const userType = localStorage.getItem("userType");
  if (userType === "driver") return driverAPI.notifications;
  return userAPI.notifications; // "client" | "guest" → user endpoints
}

// Handle notification click: open chat or navigate to the right page
function handleNotificationAction(n, navigate) {
  const userType = localStorage.getItem("userType");
  const data = n.data ?? {};
  // type and ids can live at data level or one level deeper (data.data)
  const type = data.type ?? data.data?.type ?? "";
  const bookingId = data.booking_id ?? data.data?.booking_id ?? null;
  const orderId = data.order_id ?? data.data?.order_id ?? null;
  const transportId = data.transport_id ?? data.data?.transport_id ?? null;
  const orderType =
    data.order_type ??
    data.data?.order_type ??
    (transportId ? "transport" : bookingId ? "booking" : "");
  const displayOrderId = data.data?.id ?? bookingId ?? transportId ?? orderId;

  // ── Chat notification → open the floating chat widget ────────────────
  // if (type === "chat" || data.chat_id != null) {
  //   window.dispatchEvent(new CustomEvent("openClientChat", { detail: true }));
  //   return;
  // }
  if (type === "chat") {
    window.dispatchEvent(new CustomEvent("openClientChat", { detail: true }));
    return;
  }

  // ── Driver routes ─────────────────────────────────────────────────────
  if (userType === "driver") {
    const shipmentType =
      data.booking_type ??
      (orderType === "booking"
        ? "regular"
        : data.transport_type === "car"
          ? "cars"
          : "furnitures");

    switch (type) {
      case "new_booking":
      case "transport_approved":
      case "booking_approved":
      case "booking_available":
      case "new_booking_available":
      case "new_transport_available": {
        const queryParam = type.includes("transport") 
          ? `transport_id=${displayOrderId}`
          : `booking_id=${displayOrderId}`;
        navigate(`/driver/driver-details?${queryParam}&type=${shipmentType}&open_details=true`);
        break;
      }
      case "booking_accepted":
      case "transport_accepted":
      case "booking_assigned_by_admin":
      case "transport_assigned_by_admin":
      case "price_proposal":
      case "price_accepted":
        navigate(`/driver/driver-request?type=${shipmentType}${orderId ? `&orderId=${orderId}` : ""}`);
        break;
      case "booking_request":
        navigate(`/driver/driver-request?type=${shipmentType}${orderId ? `&orderId=${orderId}` : ""}`);
        break;
      case "booking_completed":
      case "delivery_completed":
        navigate(`/driver/driver-completed?type=${shipmentType}${orderId ? `&orderId=${orderId}` : ""}`);
        break;
      case "chat_message":
        navigate(`/driver/driver-request?openChat=true&orderId=${orderId}&transportId=${transportId || bookingId}&type=${shipmentType}`);
        break;
      default:
        if (orderType === "transport" && transportId) {
          navigate(`/driver/driver-request?type=${shipmentType}${orderId ? `&orderId=${orderId}` : ""}`);
        } else if (orderType === "booking" && bookingId) {
          navigate(`/driver/driver-request?type=${shipmentType}${orderId ? `&orderId=${orderId}` : ""}`);
        } else {
          navigate("/driver/driver-details");
        }
        break;
    }
    return;
  }

  // ── Client / guest routes ─────────────────────────────────────────────
  if (type === "chat_transport_message") {
    navigate(`/user/booking/transport_details/${transportId || bookingId}?openChat=true&orderId=${orderId}`);
    return;
  }

  if (orderType === "booking" && bookingId) {
    navigate(`/user/booking/booking_details/${bookingId}`);
  } else if (orderType === "transport" && transportId) {
    navigate(`/user/booking/transport_details/${transportId}${orderId ? `?orderId=${orderId}` : ""}`);
  } else if (bookingId) {
    navigate(`/user/booking/booking_details/${bookingId}`);
  } else if (transportId) {
    navigate(`/user/booking/transport_details/${transportId}${orderId ? `?orderId=${orderId}` : ""}`);
  } else if (orderId) {
    navigate(`/user/booking`);
  } else {
    navigate("/user/booking");
  }
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Fetch all notifications (also updates unread count) ────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const res = await getNotifAPI().index();
    if (res.success) {
      const payload = res.data?.data ?? {};
      const list = payload.notifications?.data ?? [];
      setNotifications(list);
      setUnreadCount(Number(payload.unread_count ?? 0));
    }
    setLoading(false);
  }, []);

  // ── Fetch unread count (lightweight, used for badge polling) ─────────────
  const fetchUnreadCount = useCallback(async () => {
    const res = await getNotifAPI().unreadCount();
    if (res.success) {
      const count =
        res.data?.data?.unread_count ??
        res.data?.unread_count ??
        res.data?.count ??
        0;
      setUnreadCount(Number(count));
    }
  }, []);

  // Initial load + polling for badge count
  useEffect(() => {
    fetchNotifications(); // load list + count on mount
    const timer = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchNotifications, fetchUnreadCount]);

  // Re-fetch full list when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Mark single notification as read ────────────────────────────────────
  const markAsRead = async (id, isRead) => {
    if (isRead) return;
    const res = await getNotifAPI().markRead(id);
    if (res.success) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, read_at: new Date().toISOString(), read: true }
            : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  };

  // ── Mark all as read ─────────────────────────────────────────────────────
  const markAllAsRead = async () => {
    const res = await getNotifAPI().markAllRead();
    if (res.success) {
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          read_at: new Date().toISOString(),
          read: true,
        })),
      );
      setUnreadCount(0);
    }
  };

  // Helper: a notification is unread when read_at is null/undefined or read === false
  const isUnread = (n) => !n.read_at && n.read !== true;
  const displayCount = unreadCount > 9 ? "9+" : unreadCount || "";

  return (
    <div className="notif-bell-wrapper" ref={ref}>
      <button
        className="notif-bell-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={t("notifBell.title")}
      >
        {/* Bell SVG */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 && <span className="notif-badge">{displayCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          {/* Header */}
          <div className="notif-header">
            <span className="notif-title">{t("notifBell.title")}</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={markAllAsRead}>
                {t("notifBell.markAllRead")}
              </button>
            )}
          </div>

          {/* List */}
          <div className="notif-list">
            {loading ? (
              <div className="notif-empty">{t("notifBell.loading")}</div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">{t("notifBell.empty")}</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${isUnread(n) ? "notif-unread" : "notif-read"}`}
                  style={{ cursor: "pointer" }}
                  onClick={async () => {
                    await markAsRead(n.id, !isUnread(n));
                    setOpen(false);
                    handleNotificationAction(n, navigate);
                  }}
                >
                  {/* Unread indicator bar */}
                  {isUnread(n) && <span className="notif-unread-bar" />}

                  <div className="notif-content">
                    <p className="notif-item-title">
                      {translateBackendMessage(
                        n.data?.title ?? n.title,
                        "Notification",
                      )}
                    </p>
                    <p className="notif-item-msg">
                      {translateBackendMessage(
                        n.data?.body ?? n.data?.message ?? n.message,
                        "",
                      )}
                    </p>
                    <span className="notif-time">
                      {n.created_at
                        ? new Date(n.created_at).toLocaleString()
                        : (n.time ?? "")}
                    </span>
                  </div>

                  {/* Read/Unread action button */}
                  <button
                    className={`notif-read-btn ${isUnread(n) ? "notif-read-btn--unread" : "notif-read-btn--read"}`}
                    title={
                      isUnread(n)
                        ? t("notifBell.markRead")
                        : t("notifBell.read")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(n.id, !isUnread(n));
                    }}
                    disabled={!isUnread(n)}
                  >
                    {/* Checkmark SVG */}
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
